/**
 * EntryDate holds a *day*, not a moment.
 *
 * The picker gives the agent a calendar day; the column stores midnight UTC of that day.
 * Writing and reading both go through UTC so the two conversions cancel and every viewer
 * sees the day the agent picked, wherever they are.
 *
 * Both halves have to be pinned, and JavaScript changes its mind between them: a bare ISO
 * date parses as UTC, an ISO date-time without an offset parses as local. Fixing only the
 * read half passes every test written on a US machine and still ships the bug - the write
 * half is wrong only for agents east of UTC.
 */

/** Zero-pads to two digits, for the YYYY-MM-DD forms below. */
const pad = (value: number) => String(value).padStart(2, '0');

/**
 * Turns the day the agent picked into the instant to store: midnight UTC of that day.
 *
 * Reads the *local* year, month and day off the picker's Date. Deliberately not
 * `toISOString()`, which converts the local midnight it was given and so names the previous
 * day for anyone east of UTC - London and Tokyo both send the 25th for a picked 26th.
 */
export const toEntryDate = (picked: Date): string =>
  new Date(Date.UTC(picked.getFullYear(), picked.getMonth(), picked.getDate())).toISOString();

/**
 * Turns a stored value back into a Date the picker can hold: local midnight of the same
 * calendar day.
 *
 * Needed because `new Date(stored)` is the instant, and reading its *local* parts west of
 * UTC lands on the day before - so an agent correcting an entry would silently move it.
 */
export const fromEntryDate = (stored: string | Date): Date => {
  const instant = stored instanceof Date ? stored : new Date(stored);
  return new Date(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate());
};

/**
 * Formats a stored value for display, in UTC.
 *
 * The `timeZone` is the whole point: a bare `toLocaleDateString()` converts to the viewer's
 * zone and shows the previous day to anyone west of the agent.
 */
export const formatEntryDate = (stored: string | Date, locale?: string): string =>
  (stored instanceof Date ? stored : new Date(stored)).toLocaleDateString(locale, { timeZone: 'UTC' });

/**
 * The calendar day it is where this browser is, as YYYY-MM-DD, for the `x-client-date`
 * header.
 *
 * "Today" for the current-day restriction means the agent's own calendar - an agent in Tokyo
 * at 8am Monday is still on Sunday in UTC, and only the client knows the difference.
 */
export const clientDay = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

/** Same calendar day in the viewer's own timezone. Local on purpose: see clientDay. */
export const isSameLocalDay = (a: Date, b: Date = new Date()): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/* ------------------------------------------------------------------ start time --
 *
 * Once a tenant captures the start time, EntryDate stops being "midnight UTC of a day"
 * and becomes a real instant. The two shapes live in one column and are told apart by
 * HasTimeOfDay - see TicketTimeEntry.cs. Everything below deals with the second shape.
 */

/**
 * Combines a day and an HH:mm into the value to store, carrying the editor's own offset.
 *
 * Sends the wall time with its offset - "2026-09-01T05:45:00-07:00" - rather than converting to
 * UTC first. Same instant either way; the difference is that this form also records which clock
 * produced it, on the value itself.
 *
 * That replaced a separate EntryOffsetMinutes column. The column existed on the assumption that
 * the platform would normalise a DateTimeOffset to UTC on save, so an offset riding on the value
 * could not be trusted to survive. Measured 2026-09-01: it survives intact, through model
 * binding, the trigger, EF and SaveChanges. The assumption was never tested and was wrong.
 *
 * Two things follow. Power BI reads the agent's local time straight off the column instead of a
 * report author having to know to combine two fields. And the server no longer needs the
 * x-client-offset header to work out which day the agent was on - it reads the local part.
 *
 * Date-only entries are unaffected: they still store midnight at +00:00 via toEntryDate, because
 * a picked day has no clock and pinning it is what makes "the 30th" one value for every agent.
 */
export const toEntryInstant = (picked: Date, time: string): string => {
  const [h, m] = time.split(':').map(Number);
  const local = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate(), h, m, 0, 0);

  // Hand-built rather than toISOString(), which always converts to UTC and drops the offset.
  // getTimezoneOffset reports minutes to ADD to local to reach UTC, the opposite sign to the
  // ISO convention, hence the negation.
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  const off = -local.getTimezoneOffset();

  return (
    `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}` +
    `T${pad(local.getHours())}:${pad(local.getMinutes())}:00` +
    `${off < 0 ? '-' : '+'}${pad(off / 60)}:${pad(off % 60)}`
  );
};

/** The local HH:mm of a stored instant, for loading an entry back into the form. */
export const fromEntryInstant = (stored: string | Date): string => {
  const d = stored instanceof Date ? stored : new Date(stored);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * The day a stored instant falls on, in the viewer's timezone.
 *
 * Deliberately not fromEntryDate, which reads the UTC parts: that is right for a date-only
 * row, where the value is midnight UTC of the day itself, and wrong for a real instant,
 * where the UTC day can be the next one.
 */
export const localDayOf = (stored: string | Date): Date => {
  const d = stored instanceof Date ? stored : new Date(stored);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * How an entry reads: the day alone, or the day and time converted to whoever is looking.
 *
 * The two shapes are read differently on purpose, because they are different kinds of fact.
 *
 * A **date-only** row is a calendar day, not a moment. "Diego worked on the 30th" is the same
 * statement everywhere, so it is pinned to UTC and never converted - and there is nothing to
 * convert anyway, since the time is a placeholder of midnight. Converting it would move the day
 * for every viewer west of UTC on the strength of a time nobody entered.
 *
 * A row **with a start time** is a moment, and a moment is legitimately described by each
 * reader's own clock. That is the team's call, taken 2026-08-31, and it does mean the displayed
 * day can move: 8:30 AM on the 30th in Phoenix reads as the 31st in Auckland. Accepted, because
 * for a real instant that is true rather than an artefact.
 *
 * The offset now rides on the stored value rather than a separate column, so a "show in the
 * agent's timezone" option would read it straight off EntryDate.
 */
export const entryDatePart = (stored: string | Date, hasTime: boolean, locale?: string): string => {
  if (!hasTime) return formatEntryDate(stored, locale);
  const d = stored instanceof Date ? stored : new Date(stored);
  return d.toLocaleDateString(locale);
};

/** The time half, or null when the row never recorded one. */
export const entryTimePart = (
  stored: string | Date,
  hasTime: boolean,
  locale?: string
): string | null => {
  if (!hasTime) return null;
  const d = stored instanceof Date ? stored : new Date(stored);
  return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
};

/** Both halves together, for the tab. The feed wants them apart - see the two above. */
export const formatEntryWhen = (stored: string | Date, hasTime: boolean, locale?: string): string => {
  const date = entryDatePart(stored, hasTime, locale);
  const time = entryTimePart(stored, hasTime, locale);
  return time ? `${date} ${time}` : date;
};


/** Every quarter-hour of the day as HH:mm, for the restricted picker. */
export const quarterHours = (): string[] => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) for (let m = 0; m < 60; m += 15) out.push(`${pad(h)}:${pad(m)}`);
  return out;
};

/**
 * The nearest quarter at or before a time.
 *
 * Down rather than up or nearest: rounding up would put the start a few minutes after the
 * moment the agent is describing, crediting a start that has not happened.
 *
 * This used to add "and rejected by the server", which is not true and was never checked.
 * TicketTimeEntryTrigger.Validate compares whole DAYS - `entryDay > today` on a DateOnly - so
 * a start time later today passes. Nothing rejects it, and by Shaun's call on 2026-09-02
 * nothing should: booking a future time is fine as long as it is the current day.
 */
export const snapDownToQuarter = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  return `${pad(h)}:${pad(Math.floor(m / 15) * 15)}`;
};

/** Now as HH:mm, snapped down to a quarter when the tenant restricts increments. */
export const nowAsTime = (restrictToQuarters: boolean, now: Date = new Date()): string => {
  const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return restrictToQuarters ? snapDownToQuarter(time) : time;
};

/**
 * A sortable key for an entry: the day it displays, then the time within that day.
 *
 * Sorting on the raw instant is wrong once both shapes are present. A date-only row is
 * midnight UTC of its day, which west of UTC is the *evening before* in local terms - so a
 * date-only 30 Aug (30 Aug 00:00Z) sorts later than a timed 29 Aug 6pm (30 Aug 01:00Z) and
 * the list shows the 29th above the 30th.
 *
 * Keying on the displayed day fixes that, and the minutes only break ties inside a day.
 * A date-only row has no time, so it sorts to the start of its own day rather than being
 * dropped into the previous one.
 */
export const entrySortKey = (stored: string | Date, hasTime: boolean): number => {
  const d = stored instanceof Date ? stored : new Date(stored);

  // Keyed on the day each row *displays*, which is now the viewer's day for a timed row and
  // the pinned UTC day for a date-only one. Sorting on the raw instant instead would order by
  // something the list does not show: a date-only 30 Aug is 30 Aug 00:00Z, later than a timed
  // 29 Aug 6pm west of UTC, so the 29th would appear above the 30th.
  // Both branches go through Date.UTC so the two are the same kind of number. Using
  // new Date(y,m,d).getTime() for one of them keys it to local midnight while the other is
  // keyed to UTC midnight, and the pair are then hours apart for no reason the list shows -
  // enough to sort a date-only 30 Aug below a timed 29 Aug.
  const day = hasTime
    ? Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
    : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

  // A date-only row has no time, so it sorts to the start of its own day.
  const minutesIntoDay = hasTime ? d.getHours() * 60 + d.getMinutes() : 0;
  return day + minutesIntoDay * 60_000;
};
